import React, { useState } from 'react';
import { Tooltip, notification, Typography, Button, Flex } from 'antd';
import {
  LockOutlined,
  StopOutlined,
  ExportOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { getSelfOnboardingPortalUrl, getRoleNameFromToken } from '../utils/subscriptionPlan';
import {
  openManageProfileLink,
  usesManageProfileLinkForSelfOnboarding,
} from '../utils/openManageProfileLink';

const { Text } = Typography;

const VARIANTS = {
  warning: {
    icon: LockOutlined,
    stripe: 'linear-gradient(180deg, #ff7a45 0%, #fa8c16 50%, #ffc069 100%)',
    iconBg: 'linear-gradient(145deg, #ff7a45 0%, #fa541c 48%, #fa8c16 100%)',
    border: 'rgba(250, 140, 22, 0.28)',
    background: 'linear-gradient(135deg, #fff7e6 0%, #fffdf8 55%, #ffffff 100%)',
    titleColor: '#873800',
    bodyColor: 'rgba(60, 40, 20, 0.78)',
    shadow: '0 8px 24px rgba(250, 140, 22, 0.18)',
  },
  info: {
    icon: InfoCircleOutlined,
    stripe: 'linear-gradient(180deg, #69b1ff 0%, #1677ff 50%, #91caff 100%)',
    iconBg: 'linear-gradient(145deg, #4096ff 0%, #1677ff 48%, #0958d9 100%)',
    border: 'rgba(22, 119, 255, 0.22)',
    background: 'linear-gradient(135deg, #e6f4ff 0%, #f8fbff 55%, #ffffff 100%)',
    titleColor: '#003eb3',
    bodyColor: 'rgba(20, 40, 80, 0.78)',
    shadow: '0 8px 24px rgba(22, 119, 255, 0.14)',
  },
  blocked: {
    icon: StopOutlined,
    stripe: 'linear-gradient(180deg, #ff7875 0%, #ff4d4f 50%, #ffa39e 100%)',
    iconBg: 'linear-gradient(145deg, #ff7875 0%, #f5222d 48%, #cf1322 100%)',
    border: 'rgba(255, 77, 79, 0.24)',
    background: 'linear-gradient(135deg, #fff1f0 0%, #fffafa 55%, #ffffff 100%)',
    titleColor: '#820014',
    bodyColor: 'rgba(80, 20, 20, 0.78)',
    shadow: '0 8px 24px rgba(255, 77, 79, 0.14)',
  },
};

const iconWrapStyle = (variant) => ({
  width: 32,
  height: 32,
  borderRadius: 10,
  background: variant.iconBg,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#fff',
  fontSize: 15,
  flexShrink: 0,
  boxShadow: '0 3px 10px rgba(0, 0, 0, 0.12)',
});

export function SubscriptionRestrictionTooltipContent({
  title,
  message,
  variant = 'warning',
  showPortalLink = true,
  compact = false,
  roleName,
}) {
  const authToken = useSelector((state) => state.auth.token);
  const [portalLoading, setPortalLoading] = useState(false);
  const theme = VARIANTS[variant] || VARIANTS.warning;
  const Icon = theme.icon;
  const portalUrl = getSelfOnboardingPortalUrl();
  const resolvedRole = roleName || getRoleNameFromToken(authToken);
  const useManageProfileLink = usesManageProfileLinkForSelfOnboarding(resolvedRole, authToken);
  const canShowPortalButton =
    showPortalLink && (useManageProfileLink || portalUrl);

  const handleOpenPortal = async () => {
    setPortalLoading(true);
    try {
      await openManageProfileLink(authToken);
    } finally {
      setPortalLoading(false);
    }
  };

  return (
    <div
      style={{
        position: 'relative',
        maxWidth: compact ? 280 : 320,
        borderRadius: 12,
        overflow: 'hidden',
        border: `1px solid ${theme.border}`,
        background: theme.background,
        boxShadow: theme.shadow,
      }}
    >
      <div
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: 3,
          background: theme.stripe,
        }}
      />
      <div style={{ padding: compact ? '10px 12px 10px 14px' : '12px 14px 12px 16px' }}>
        <Flex align="flex-start" gap={10}>
          <div style={iconWrapStyle(theme)}>
            <Icon />
          </div>
          <div style={{ minWidth: 0, flex: 1 }}>
            {title && (
              <Text
                strong
                style={{
                  display: 'block',
                  color: theme.titleColor,
                  fontSize: compact ? 13 : 14,
                  lineHeight: 1.35,
                  marginBottom: 4,
                }}
              >
                {title}
              </Text>
            )}
            {typeof message === 'string' ? (
              <Text
                style={{
                  display: 'block',
                  color: theme.bodyColor,
                  fontSize: compact ? 12 : 13,
                  lineHeight: 1.5,
                }}
              >
                {message}
              </Text>
            ) : (
              <div
                style={{
                  color: theme.bodyColor,
                  fontSize: compact ? 12 : 13,
                  lineHeight: 1.5,
                }}
              >
                {message}
              </div>
            )}
            {canShowPortalButton && (
              <Button
                type="link"
                size="small"
                href={useManageProfileLink ? undefined : portalUrl}
                target={useManageProfileLink ? undefined : '_blank'}
                rel={useManageProfileLink ? undefined : 'noopener noreferrer'}
                onClick={useManageProfileLink ? handleOpenPortal : undefined}
                loading={portalLoading}
                icon={<ExportOutlined />}
                style={{
                  padding: 0,
                  height: 'auto',
                  marginTop: 8,
                  fontSize: 12,
                  color: theme.titleColor,
                }}
              >
                Manage Subscription
              </Button>
            )}
          </div>
        </Flex>
      </div>
    </div>
  );
}

export default function SubscriptionRestrictionTooltip({
  title,
  message,
  variant = 'warning',
  placement = 'bottom',
  mouseEnterDelay = 0.15,
  showPortalLink = true,
  compact = false,
  children,
}) {
  if (!message) {
    return children;
  }

  return (
    <Tooltip
      title={
        <SubscriptionRestrictionTooltipContent
          title={title}
          message={message}
          variant={variant}
          showPortalLink={showPortalLink}
          compact={compact}
        />
      }
      placement={placement}
      mouseEnterDelay={mouseEnterDelay}
      color="transparent"
      overlayInnerStyle={{
        padding: 0,
        background: 'transparent',
        boxShadow: 'none',
      }}
    >
      {children}
    </Tooltip>
  );
}

let lastRestrictionNotifyAt = 0;

/** Styled bottom-left toast for subscription / plan restriction messages. */
export function notifySubscriptionRestriction({
  title,
  message,
  variant = 'warning',
  showPortalLink = true,
  duration = 6,
}) {
  const now = Date.now();
  if (now - lastRestrictionNotifyAt < 3000) {
    return;
  }
  lastRestrictionNotifyAt = now;

  notification.open({
    key: 'subscription-restriction',
    placement: 'bottomLeft',
    duration,
    message: null,
    description: (
      <SubscriptionRestrictionTooltipContent
        title={title}
        message={message}
        variant={variant}
        showPortalLink={showPortalLink}
      />
    ),
    style: {
      padding: 0,
      background: 'transparent',
      boxShadow: 'none',
      width: 360,
      maxWidth: 'calc(100vw - 32px)',
    },
    className: 'subscription-restriction-notification',
  });
}
